<template>
  <v-container
    class="dashboard-presentation py-0 my-0"
    :class="{ 'py-md-4': emailFunnelsLoading || analyticsImpressionsLoading }"
    fluid
  >
    <DetailsDialog
      v-if="showDialog"
      @close-details="showDialog = false"
      :component="component"
      :component-value="componentValue"
    />
    <v-row v-show="emailFunnelsLoading || analyticsImpressionsLoading">
      <v-col
        class="pa-0 ma-0 pr-md-2"
        cols="12"
        md="6"
      >
        <v-skeleton-loader
          max-height="300px"
          style="background: #1d8a86; border-radius: 15px"
          class="elevation-1 rounded-md"
          width="100%"
          type="table-heading, article, actions"
        />
        <v-skeleton-loader
          max-height="200px"
          style="background: #229e9a; border-radius: 15px"
          class="elevation-1 rounded-md mx-auto"
          width="95%"
          type="article"
        />
        <v-skeleton-loader
          max-height="300px"
          style="background: #26b1ad; border-radius: 15px"
          class="elevation-1 rounded-md mx-auto"
          width="90%"
          type="table-heading, article, actions"
        />
        <v-skeleton-loader
          max-height="250px"
          style="background: #2ac5c0; border-radius: 15px"
          class="elevation-1 rounded-md mx-auto"
          width="85%"
          type="article"
        />
      </v-col>
      <v-col
        class="pa-0 ma-0 pr-md-2"
        cols="12"
        md="6"
      >
        <v-skeleton-loader
          max-height="300px"
          width="100%"
          type="image"
        />
      </v-col>
    </v-row>
    <v-row
      v-show="!emailFunnelsLoading && !analyticsImpressionsLoading"
      :class="{ 'mt-0': !emailFunnelsLoading && !analyticsImpressionsLoading }"
    >
      <!-- Left Column -->
      <v-col
        v-show="!emailFunnelsLoading && !analyticsImpressionsLoading"
        class="pa-0 ma-0 pr-md-2"
        cols="12"
        md="6"
      >
        <v-card
          border
          class="box px-4 py-0 pt-md-4 mr-md-2"
        >
          <v-container
            class="px-0 py-0 pt-4 pb-md-4"
            fluid
          >
            <v-row>
              <v-col
                v-if="isNewUser"
                class="pa-0 ma-0"
                cols="12"
              >
                <GainedContacts
                  :statistics="statistics"
                  :loading="loading"
                  :is-new-user="isNewUser"
                />
              </v-col>
              <v-col
                class="pa-0 ma-0"
                cols="12"
              >
                <ContactAcquisition
                  @update-stats="handleContactAquisitionStatsUpdate"
                  @open-details="
                    (openDetails('contactAcquisitionDetails', $event), (showDialog = true))
                  "
                  :is-light-user="isLightUser"
                  :is-new-user="isNewUser"
                />
              </v-col>
              <v-col
                class="pa-0 ma-0"
                cols="12"
              >
                <Impressions
                  @update-is-new-user="handleAnalyticsImpressions"
                  @analytics-impressions-loading-update="handleAnalyticsImpressionsLoading"
                  @open-details="(openDetails('impressionsDetails'), (showDialog = true))"
                  :is-new-user="isNewUser"
                  :is-light-user="isLightUser"
                />
              </v-col>
              <v-col
                class="pa-0 ma-0"
                cols="12"
              >
                <EmailFunnels
                  @update-is-new-user="handleSentEmailStatsUpdate"
                  @email-funnel-loading-update="handleEmailFunnelsLoading"
                  @update-stats="handleEmailFunnelsStatsUpdate"
                  @open-details="(openDetails('emailFunnelsDetails'), (showDialog = true))"
                  :is-new-user="isNewUser"
                  :is-light-user="isLightUser"
                />
              </v-col>
              <v-col
                v-if="!isNewUser"
                class="pa-0 ma-0"
                cols="12"
              >
                <GainedContacts
                  :statistics="statistics"
                  :loading="loading"
                  :is-new-user="isNewUser"
                  :is-light-user="isLightUser"
                  @open-details="(openDetails('gainedContactsDetails'), (showDialog = true))"
                />
              </v-col>
              <v-col
                class="pa-0 ma-0"
                cols="12"
              >
                <AddedValue
                  :statistics="statistics"
                  :loading="loading"
                  :total-time-saved="totalTimeSaved"
                  :total-money-saved="totalMoneySaved"
                  v-if="!isNewUser"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-col>

      <!-- Right Column -->
      <v-col
        v-show="leads"
        class="pa-0 ma-0 mt-4 mt-md-0"
        cols="12"
        md="6"
      >
        <Permission permission="lead:read">
          <Feature
            v-slot="{ feature }"
            :feature-slug="featureNames.COMPANY"
          >
            <Feature
              :feature-slug="featureNames.VALUATION"
              class="w-10"
            >
              <lead-map :leads="leads" />
            </Feature>
            <lead-interactivity
              class="mt-4"
              v-if="feature.config.showLeadInteractivity"
            />
          </Feature>
        </Permission>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable'
import { computed, inject, PropType, ref } from 'vue'

import Permission from '@/components/Permission'
import featureNames from '@/lib/featureNames'
import { ProductType } from '@/lib/productTypes'
import COMPANY from '@/modules/company/queries/Company.gql'
import AddedValue from '@/modules/dashboard/AddedValue.vue'
import ContactAcquisition from '@/modules/dashboard/ContactAcquisition.vue'
import DetailsDialog from '@/modules/dashboard/dashboardDetails/detailsDialog.vue'
import EmailFunnels from '@/modules/dashboard/EmailFunnels.vue'
import GainedContacts from '@/modules/dashboard/GainedContacts.vue'
import Impressions from '@/modules/dashboard/Impressions.vue'
import LeadMap from '@/modules/map/index.vue'
import LeadInteractivity from '@/modules/statistics/LeadInteractivity.vue'
import { Feature } from '@/plugins/feature-config'

defineProps({
  statistics: Object as PropType<Record<string, any>>,
  leads: Object as PropType<Record<string, any>>,
  loading: Boolean,
})

const $auth = inject('auth')

const contactAquisitionTimeSaved = ref(0)
const contactAquisitionMoneySaved = ref(0)
const emailFunnelsTimeSaved = ref(0)
const emailFunnlesMoneySaved = ref(0)
const emailFunnelsSentEmails = ref(0)
const analyticsImpressions = ref(0)
const emailFunnelsLoading = ref(true)
const analyticsImpressionsLoading = ref(true)

const { result: companyResluts } = useQuery(
  COMPANY,
  { id: $auth.user.companyId },
  {
    fetchPolicy: 'cache-first',
  }
)

const handleEmailFunnelsLoading = (loading: boolean) => {
  emailFunnelsLoading.value = loading
}
const handleAnalyticsImpressionsLoading = (loading: boolean) => {
  analyticsImpressionsLoading.value = loading
}

const isLightUser = computed(
  () => companyResluts?.value?.company?.productType === ProductType.LIGHT
)

const totalTimeSaved = computed(
  () => contactAquisitionTimeSaved.value + emailFunnelsTimeSaved.value
)
const totalMoneySaved = computed(
  () => contactAquisitionMoneySaved.value + emailFunnlesMoneySaved.value
)

interface StatsUpdate {
  timeSaved: number
  moneySaved: number
}

const handleContactAquisitionStatsUpdate = ({ timeSaved, moneySaved }: StatsUpdate) => {
  contactAquisitionTimeSaved.value = timeSaved
  contactAquisitionMoneySaved.value = moneySaved
}

const handleEmailFunnelsStatsUpdate = ({ timeSaved, moneySaved }: StatsUpdate) => {
  emailFunnelsTimeSaved.value = timeSaved
  emailFunnlesMoneySaved.value = moneySaved
}

const isNewUser = computed(() => {
  return !(emailFunnelsSentEmails.value > 0 && analyticsImpressions.value > 0)
})

const handleSentEmailStatsUpdate = ({ sentEmails }: { sentEmails: number }) => {
  emailFunnelsSentEmails.value = sentEmails ?? 0
}

const handleAnalyticsImpressions = ({ analyticsImpression }: { analyticsImpression: number }) => {
  analyticsImpressions.value = analyticsImpression ?? 0
}

const openDetails = (newComponent, data = null) => {
  component.value = newComponent
  componentValue.value = data ?? null
}
const showDialog = ref(false)
const component = ref('contactAcquisitionDetails')
const componentValue = ref(null)
</script>

<style>
.dashboard-presentation {
  .card {
    border-radius: 10px;
    color: #ffffff;
    padding: 10px 15px;
    justify-content: center;
    align-items: center;
    gap: 2px;
  }
  .card-new-user {
    border-radius: 10px;
    color: #ffffff;
    padding: 10px 15px;
    justify-content: center;
    align-items: center;
    gap: 2px;
  }
  .vertical-divider {
    font-size: 22px;
    opacity: 0.3;
    color: #ffffff;
  }
  .save-tme-money-numbers {
    display: flex;
    align-items: center;
  }
  .save-time-money {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .funnel,
  .funnel-new-user {
    margin: 0 auto;
    position: relative;
    margin-bottom: 10px;
  }

  .grid {
    display: grid;
  }

  .icon {
    font-size: 18px;
  }

  .title {
    color: #ffffff;
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: 26px;
  }

  .label {
    color: #ffffff;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
  }

  .number {
    color: #ffffff;
    font-size: 22px;
    font-style: normal;
    font-weight: 700;
    line-height: 28px;
  }

  .button {
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 16px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  .button-invite-more,
  .button-do-more {
    width: 100%;
  }

  @media (max-width: 960px) {
    .funnel,
    .funnel-new-user {
      width: 100%;
    }
    .funnel::after {
      left: 6px !important;
      right: 6px !important;
    }
    .funnel-new-user::after {
      left: 8px !important;
      right: 8px !important;
    }
    .title {
      font-size: 14px;
    }
    .label {
      font-size: 12px;
    }
    .number {
      font-size: 16px;
    }
    .button {
      font-size: 12px;
    }
    .v-btn--size-default {
      width: 100%;
    }

    .icon {
      font-size: 16px;
    }
  }
}
</style>
